var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { useEffect, useImperativeHandle, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import classnames from 'classnames';
import { CloseButton } from './CloseButton';
export var Modal = React.forwardRef(function (_a, ref) {
    var children = _a.children, onClick = _a.onClick, ariaLabelledby = _a.ariaLabelledby, className = _a.className, _b = _a.locale, locale = _b === void 0 ? 'nb' : _b, onClose = _a.onClose, portalContainer = _a.portalContainer, rest = __rest(_a, ["children", "onClick", "ariaLabelledby", "className", "locale", "onClose", "portalContainer"]);
    // SSR does not have access to document/window etc.
    var _c = useState(false), isClient = _c[0], setIsClient = _c[1];
    var _d = useState(false), isOpen = _d[0], setIsOpen = _d[1];
    var dialogRef = useRef(null);
    var htmlOverflowY = useRef(isClient ? document.documentElement.style.overflowY : '');
    useImperativeHandle(ref, function () { return ({
        open: function () {
            // Prevent opening an already open modal
            if (!isOpen && dialogRef.current) {
                setIsOpen(true);
                dialogRef.current.showModal();
            }
        },
        close: function () {
            // Prevent closing an already closed modal
            if (isOpen && dialogRef.current) {
                dialogRef.current.close();
            }
        },
        // Expose isOpen state
        get isOpen() {
            return isOpen;
        },
        // Expose dialogRef directly
        dialogRef: dialogRef,
    }); });
    useEffect(function () {
        setIsClient(true);
    }, []);
    useEffect(function () {
        var _a;
        var inShadow = isClient &&
            ((_a = dialogRef.current) === null || _a === void 0 ? void 0 : _a.getRootNode()) instanceof ShadowRoot;
        if (inShadow) {
            var html = document.documentElement;
            if (isOpen) {
                htmlOverflowY.current = html.style.overflowY;
                html.style.overflowY = 'hidden';
            }
            else if (!isOpen) {
                html.style.overflowY = htmlOverflowY.current;
            }
        }
    }, [isClient, isOpen]);
    useEffect(function () {
        if (isOpen && dialogRef.current) {
            dialogRef.current.focus();
        }
    }, [isOpen]);
    useEffect(function () {
        if (isClient &&
            dialogRef.current &&
            typeof dialogRef.current.showModal !== 'function') {
            import('dialog-polyfill').then(function (_a) {
                var dialogPolyfill = _a.default;
                if (dialogRef.current) {
                    dialogPolyfill.registerDialog(dialogRef.current);
                }
            });
        }
    }, [isClient]);
    return isClient
        ? createPortal(
        // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
        React.createElement("dialog", __assign({}, rest, { ref: dialogRef, className: classnames('ffe-modal', className), "aria-labelledby": ariaLabelledby, onClick: function (event) {
                var target = event.target;
                if (target.nodeName === 'DIALOG') {
                    target.close();
                }
                onClick === null || onClick === void 0 ? void 0 : onClick(event);
            }, onClose: function () {
                onClose === null || onClose === void 0 ? void 0 : onClose();
                setIsOpen(false);
            } }),
            React.createElement("div", { className: "ffe-modal__body" },
                React.createElement(CloseButton, { onClick: function () { var _a; return (_a = dialogRef.current) === null || _a === void 0 ? void 0 : _a.close(); }, locale: locale }),
                children)), portalContainer !== null && portalContainer !== void 0 ? portalContainer : document.body)
        : null;
});
